import type { CSSProperties } from 'react';
import { createBrowserRouter, Navigate, NavLink, Outlet } from 'react-router-dom';
import { useBreadcrumbStore } from './breadcrumb-store';
import { LoginPage } from '../pages/login-page';
import { ConsolePage } from '../pages/console-page';
import { DashboardPage } from '../pages/dashboard-page';
import { DevicesPage } from '../pages/devices-page';
import { PathsPage } from '../pages/paths-page';
import { MountPointsPage } from '../pages/mount-points-page';
import { AgentsPage } from '../pages/agents-page';
import { TeamsPage } from '../pages/teams-page';
import { TeamTemplatesPage } from '../pages/team-templates-page';
import { MasterManagementPage } from '../pages/master-management-page';
import { CommandMasterPage } from '../pages/command-master-page';
import { SystemSettingsPage } from '../pages/system-settings-page';
import { ClaudeAuthMasterPage } from '../pages/claude-auth-master-page';
import { MountPurposesPage } from '../pages/mount-purposes-page';
import { ConductorsPage } from '../pages/conductors-page';
import { ProjectsPage } from '../pages/projects-page';
import { AssignPage } from '../pages/assign-page';
import { MonitorOverviewPage } from '../pages/monitor-page';
import { SchedulesPage } from '../pages/schedules-page';
import { RequestLogsPage } from '../pages/request-logs-page';
import { OfflineQueuePage } from '../pages/offline-queue-page';
import { SettingsLayout } from './settings-layout';
import { AppLayout } from './app-layout';
import { useAuthStore } from '../auth/auth-store';

// M1-E3-S2-T2 / FR-09: 認証必須ルーティング。token なしならログインへ。
function RequireAuth() {
  const token = useAuthStore((s) => s.accessToken);
  if (!token) return <Navigate to="/login" replace />;
  return <Outlet />;
}

function RedirectIfAuthed() {
  const token = useAuthStore((s) => s.accessToken);
  if (token) return <Navigate to="/" replace />;
  return <LoginPage />;
}

// チャット（会話）＝主役を index に。デバイス / プロジェクト等のマスタは /settings 配下（脇役）。
export const router = createBrowserRouter([
  {
    path: '/login',
    element: <RedirectIfAuthed />,
  },
  {
    path: '/',
    element: <RequireAuth />,
    children: [
      {
        path: '',
        element: <AppLayout nav={<MainNav />} />,
        children: [
          { index: true, element: <ConsolePage /> },
          {
            path: 'settings',
            element: <SettingsLayout />,
            children: [
              { index: true, element: <Navigate to="devices" replace /> },
              { path: 'mount-points', element: <MountPointsPage /> },
              { path: 'devices', element: <DevicesPage /> },
              { path: 'paths', element: <PathsPage /> },
              { path: 'agents', element: <AgentsPage /> },
              { path: 'teams', element: <TeamsPage /> },
              { path: 'team-templates', element: <TeamTemplatesPage /> },
              { path: 'masters', element: <MasterManagementPage /> },
              {
                path: 'commands',
                children: [
                  { index: true, element: <Navigate to="global" replace /> },
                  { path: 'global', element: <CommandMasterPage scope="global" /> },
                  { path: 'project', element: <CommandMasterPage scope="project" /> },
                  { path: 'team', element: <CommandMasterPage scope="team" /> },
                ],
              },
              { path: 'system', element: <SystemSettingsPage /> },
              { path: 'claude-auth', element: <ClaudeAuthMasterPage /> },
              { path: 'mount-purposes', element: <MountPurposesPage /> },
              { path: 'conductors', element: <ConductorsPage /> },
              { path: 'projects', element: <ProjectsPage /> },
              { path: 'assign', element: <AssignPage /> },
              { path: 'schedules', element: <SchedulesPage /> },
              { path: 'overview', element: <MonitorOverviewPage /> },
              { path: 'dashboard', element: <DashboardPage /> },
              { path: 'request-logs', element: <RequestLogsPage /> },
              { path: 'offline-queue', element: <OfflineQueuePage /> },
            ],
          },
        ],
      },
    ],
  },
]);

function MainNav() {
  const crumb = useBreadcrumbStore((s) => s.crumb);
  return (
    <nav className="k-mobile-hidden" style={{ display: 'flex', gap: 20, fontSize: 15, alignItems: 'center', minWidth: 0 }}>
      <NavLink to="/" end style={navLinkStyle}>
        💬 チャット
      </NavLink>
      <NavLink to="/settings" style={navLinkStyle}>
        ⚙ 設定
      </NavLink>
      {crumb && (crumb.project || crumb.team || crumb.thread) && (
        // チャット画面で選択中の プロジェクト › チーム › スレッド を「設定」の横に出す（モバイルは階層ナビで見えるので非表示）
        <span className="k-mobile-hidden" style={bcWrap}>
          {crumb.project && <span style={bcProject}>📁 {crumb.project}</span>}
          {crumb.team && (
            <>
              <span style={bcSep}>›</span>
              <span style={bcTeam}>{crumb.team}</span>
            </>
          )}
          {crumb.thread && (
            <>
              <span style={bcSep}>›</span>
              <span style={bcThread}>💬 {crumb.thread}</span>
            </>
          )}
        </span>
      )}
    </nav>
  );
}

const navLinkStyle = ({ isActive }: { isActive: boolean }): CSSProperties => ({
  color: isActive ? '#0f172a' : '#475569',
  fontWeight: isActive ? 700 : 500,
  whiteSpace: 'nowrap',
});

const bcWrap: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  minWidth: 0,
  paddingLeft: 16,
  marginLeft: 4,
  borderLeft: '1px solid #e2e8f0',
  fontSize: 13,
  overflow: 'hidden',
};
const bcProject: CSSProperties = {
  fontWeight: 700,
  color: '#0f172a',
  whiteSpace: 'nowrap',
  flexShrink: 0,
};
const bcSep: CSSProperties = { color: '#cbd5e1', fontWeight: 700, flexShrink: 0 };
const bcTeam: CSSProperties = {
  fontWeight: 700,
  color: '#3730a3',
  background: '#eef2ff',
  borderRadius: 999,
  padding: '2px 10px',
  whiteSpace: 'nowrap',
  flexShrink: 0,
};
const bcThread: CSSProperties = {
  fontWeight: 600,
  color: '#475569',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
};
